import React from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

type PageStatusTone = "loading" | "error" | "denied";

/**
 * Estado de página centralizado na área de conteúdo dos módulos de Operação:
 * carregamento, falha (com tentativa novamente) ou falta de permissão. Ocupa
 * uma altura mínima estável para que a troca pelo conteúdo não desloque o
 * layout.
 *
 * O carregamento segue o padrão visual da Distribuição: spinner verde
 * discreto ao lado de uma mensagem curta. Ele surge com um pequeno atraso
 * (`animate-status-in`) para não piscar em consultas rápidas; o conteúdo
 * substitui o estado assim que fica pronto, sem espera artificial.
 */
export function PageStatus({
  tone,
  title,
  description,
  actions,
}: {
  tone: PageStatusTone;
  title: string;
  description?: string;
  actions?: React.ReactNode;
}) {
  if (tone === "loading") {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        data-testid="page-status-loading"
        className="flex min-h-[40vh] items-center justify-center px-4 py-12"
      >
        <div className="flex animate-status-in items-center gap-2 text-foreground-muted">
          <Loader2
            aria-hidden="true"
            size={24}
            className="shrink-0 animate-spin text-brand-dark motion-reduce:animate-none"
          />
          <span>{title}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      aria-live={tone === "error" ? undefined : "polite"}
      data-testid={`page-status-${tone}`}
      className="flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4 py-10 text-center"
    >
      {tone === "error" ? (
        <AlertTriangle aria-hidden="true" size={24} className="text-red-600" />
      ) : null}
      <div className="max-w-md space-y-1">
        <p className={`text-sm font-semibold ${tone === "error" ? "text-red-700" : "text-foreground"}`}>
          {title}
        </p>
        {description ? (
          <p className="text-sm text-foreground-muted">{description}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex w-full max-w-xs flex-col gap-2 sm:w-auto sm:max-w-none sm:flex-row">
          {actions}
        </div>
      ) : null}
    </div>
  );
}
